import * as classNames from 'classnames';
import * as React from 'react';
import {VideoSettings as Settings} from '../video-settings';
import {Video} from './video';
import {VideoStatusBar} from './status-bar';
import {VideoStatusMessage} from './status-message';
import {SessionType} from '../../redux/modules/session-router/reducer';
import {WS_SEND_UPDATE_STATUS} from '../../redux/modules/web-socket/constants';
import {isMobile} from '../../utils/helpers';
import {IStatusMessage, statusToColor, selfStatusToMessage, peerStatusToMessage} from './service';

import {
  STATUS_DISCONNECTED,
} from '../../redux/modules/video/constants';

import {
    wrap            as cssWrap,
    container       as cssContainer,
    main            as cssMain,
    settings        as cssSettings,
    settingsBig     as cssSettingsBig,
} from './video-pane.module.sass';

export interface IVideoContainerConnectedProps {
  audioOutputDeviceId: string;
  audioInputDeviceId: string;
  videoInputDeviceId: string;
  videoQualityId: string;
  isInterviewer: boolean;
  sessionType: SessionType;
  selfStatus: string;
  peerStatus: string;
  startTime: Date;
  token: string;
}

export interface IVideoContainerProps extends IVideoContainerConnectedProps {
    runCommand: (command: string, data: any) => void;
    draggable?: boolean;
    room: any;
    isShowingPeerScreen?: boolean;
    isSendingScreen?: boolean;
    sendScreen?: () => void;
}

export interface IVideoContainerState {
    showSettings: boolean;
    isReceivingScreen: boolean;
}

export class VideoContainer extends React.Component<IVideoContainerProps, IVideoContainerState> {
    public state = {
        showSettings: false,
        isReceivingScreen: false,
    };

  private peerStatusChangedAt: Date = null;
  private timerId: number | undefined;

  public componentWillUnmount(): void {
    clearTimeout(this.timerId);
  }

  public componentDidUpdate({selfStatus, peerStatus}: IVideoContainerProps): void {
    if (selfStatus !== this.props.selfStatus) {
      this.props.runCommand(WS_SEND_UPDATE_STATUS, {status: this.props.selfStatus});
    }

    if (peerStatus !== this.props.peerStatus) {
      this.peerStatusChangedAt = new Date();
    }
  }

  public render(): React.ReactElement<any> {
    const {
      token,
      room,
      selfStatus,
      peerStatus,
      videoQualityId,
      audioInputDeviceId,
      videoInputDeviceId,
      audioOutputDeviceId,
      draggable = false,
      isShowingPeerScreen = false,
      isSendingScreen = false,
      sendScreen,
    } = this.props;

    const {showSettings} = this.state;
    const statusMessage = this.getStatusMessage();

    return (
      <div className={cssWrap}>
        <div className={cssContainer}>
          {!isMobile() &&
            <div
              className={classNames(cssSettings, {[cssSettingsBig]: !draggable})}
              onClick={this.onShowSettings}
            >
              <i className='fa fa-gear' />
            </div>
          }

          <div className={cssMain}>
            {statusMessage.show &&
              <VideoStatusMessage
                title={statusMessage.title}
                subTitle={statusMessage.message}
                loader={statusMessage.loader}
              />
            }

            <Video
              audioOutputDeviceId={audioOutputDeviceId}
              audioInputDeviceId={audioInputDeviceId}
              videoInputDeviceId={videoInputDeviceId}
              videoQualityId={videoQualityId}
              visible={statusMessage.videoVisible}
              room={room}
              token={token}
              onParticipantDisconnected={this.onParticipantDisconnected}
              onReceiveScreen={this.onReceiveScreen}
              draggable={draggable}
              isShowingPeerScreen={isShowingPeerScreen}
            />
          </div>

          {statusMessage.videoVisible &&
            <VideoStatusBar
              selfColor={statusToColor(selfStatus)}
              peerColor={statusToColor(peerStatus)}
              isSendingScreen={isSendingScreen}
              sendScreen={sendScreen}
            />
          }

          <Settings
            context={'session'}
            isOpen={showSettings}
            onClose={this.onHideSettings}
            onSave={this.onHideSettings}
          />
        </div>
      </div>
    );
  }

    private getStatusMessage(): IStatusMessage {
        const {
            peerStatusChangedAt,
            props: {
                selfStatus,
                peerStatus,
                startTime,
                isInterviewer,
                sessionType,
                isShowingPeerScreen = false,
            },
            state: {isReceivingScreen},
        } = this;

        const selfStatusMessage = selfStatusToMessage(selfStatus);
        if (selfStatusMessage.show) {
            return selfStatusMessage;
        }

        const peerStatusMessage = peerStatusToMessage(
            peerStatus, peerStatusChangedAt, startTime, isInterviewer, sessionType,
        );
        if (peerStatusMessage.show) {
            return peerStatusMessage;
        }

        if (isShowingPeerScreen && !isReceivingScreen) {
            return {
                show: true,
                loader: false,
                videoVisible: true,
                title: "Your peer hasn't shared their screen",
                message: 'If they choose to share their screen, it will appear here.',
            };
        }

        return {
            show: false,
            loader: false,
            videoVisible: true,
            title: '',
            message: '',
        };
    }

  private onShowSettings = () => {
    this.setState({showSettings: true});
  }

  private onHideSettings = () => {
    this.setState({showSettings: false});
  }

  private onParticipantDisconnected = (): void => {
    // try to set status to disconnected when peer has left
    // trigger re-render after 5s to show another disconnected message
    // @todo need to investigate this case in order to implement appropriate solution instead of trigger re-render
    if (this.props.peerStatus === STATUS_DISCONNECTED) {
      this.timerId = window.setTimeout(() => this.setState({}), 5 * 1000);
    }
  };

    private onReceiveScreen = () => this.setState({isReceivingScreen: true});
}
